import { BarChart3, ChevronDown, FolderOpen } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'

import { Badge } from '@/components/ui/badge'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { RecordFileExplorer } from '@/features/records/file-explorer/RecordFileExplorer'
import { useAllowedActions, usePermission } from '@/hooks/usePermission'
import { useRecordGroup, useRecordGroupSummary } from '@/hooks/useRecordGroups'
import { useRecordSummary } from '@/hooks/useRecords'
import { PERMISSION_KEYS } from '@/stores/permission.store'
import { useSubscriptionStore } from '@/stores/subscription.store'
import type { RecordGroupDetail } from '@/types/api/record-group'
import { cn } from '@/utils/cn'

import { RecordStats } from './RecordStats'
import { useGroupSections } from './useGroupSections'

interface GroupSidebarProps {
  group: RecordGroupDetail | null
}

export function GroupSidebar({ group }: GroupSidebarProps) {
  const { recordGroupId: routeGroupId } = useParams<{ recordGroupId: string }>()
  const location = useLocation()
  const routePreview = (location.state as { recordGroupPreview?: { id: string; name: string } } | null)?.recordGroupPreview
  const heading = routePreview && routePreview.id === routeGroupId ? routePreview.name : group?.name ?? 'Record Group'
  const [statsOpen, setStatsOpen] = useState(false)
  const [isNarrowViewport, setIsNarrowViewport] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches,
  )
  const statsHoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lockedResourceIds = useSubscriptionStore((state) => state.lockedResourceIds)
  const canCreateGroups = usePermission().can(PERMISSION_KEYS.RECORD_GROUP_CREATE)
  const rootGroupId = group?.ancestors[0]?.id ?? group?.id
  const { data: fetchedRootGroup } = useRecordGroup(rootGroupId)
  const rootGroup = group?.id === rootGroupId ? group : fetchedRootGroup ?? null
  const groupActions = useAllowedActions('RECORD_GROUP', rootGroup?.id)
  const canCreateChild = canCreateGroups && groupActions.can(PERMISSION_KEYS.RECORD_GROUP_UPDATE)
  const statsGroupId = group && !group.traceOnly ? group.id : undefined
  const { data: recordSummary, isLoading: isRecordSummaryLoading } = useRecordSummary(statsGroupId)
  const { data: recordGroupSummary } = useRecordGroupSummary(statsGroupId)
  const sections = useGroupSections(group)
  const activeGroupPathIds = group ? [...group.ancestors.map((ancestor) => ancestor.id), group.id] : []

  useEffect(() => () => {
    if (statsHoverTimer.current) clearTimeout(statsHoverTimer.current)
  }, [])

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 767px)')
    const updateViewport = () => setIsNarrowViewport(mediaQuery.matches)
    updateViewport()
    mediaQuery.addEventListener('change', updateViewport)
    return () => mediaQuery.removeEventListener('change', updateViewport)
  }, [])

  const groupSummaryCard = (
    <div className="flex min-w-0 items-center gap-3 rounded-xl border border-border bg-background p-2.5 md:rounded-none md:border-0 md:bg-transparent md:px-4 md:py-4">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary md:h-10 md:w-10">
          <FolderOpen aria-hidden="true" className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 flex-col items-start gap-1.5 md:flex-row md:flex-nowrap md:items-center md:gap-2">
            <h1 className="truncate text-lg font-semibold text-foreground">{heading}</h1>
            {rootGroup && (rootGroup.formId ? <Badge variant="success">Form configured</Badge> : <Badge variant="secondary">No form configured</Badge>)}
          </div>
          <div className="mt-1 flex flex-col items-start gap-1.5 md:flex-row md:flex-wrap md:items-center md:gap-2">
            {statsGroupId && (
              <Popover open={statsOpen} onOpenChange={setStatsOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    onMouseEnter={() => {
                      statsHoverTimer.current = setTimeout(() => setStatsOpen(true), 1000)
                    }}
                    onMouseLeave={() => {
                      if (statsHoverTimer.current) clearTimeout(statsHoverTimer.current)
                      statsHoverTimer.current = null
                    }}
                    className="inline-flex items-center gap-1 rounded-full border border-border bg-secondary px-2.5 py-0.5 text-xs font-medium text-secondary-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <BarChart3 aria-hidden="true" className="h-3 w-3" />
                    <span className="md:hidden">View stats</span><span className="hidden md:inline">View Stats</span>
                    <ChevronDown aria-hidden="true" className="hidden h-3 w-3 md:block" />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  side={isNarrowViewport ? 'bottom' : 'right'}
                  align={isNarrowViewport ? 'center' : 'start'}
                  collisionPadding={12}
                  className="max-h-[calc(100dvh-1.5rem)] w-[calc(100vw-2rem)] max-w-3xl overflow-y-auto p-4"
                >
                  <RecordStats
                    summary={recordSummary}
                    groupSummary={recordGroupSummary}
                    isLoading={isRecordSummaryLoading}
                  />
                </PopoverContent>
              </Popover>
            )}
          </div>
        </div>
        {rootGroup && (rootGroup.childCount > 0 || canCreateChild) && (
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:hidden"
              >
                Child groups{rootGroup.childCount > 0 ? ` (${rootGroup.childCount})` : ''}
                <ChevronDown aria-hidden="true" className="h-3.5 w-3.5" />
              </button>
            </PopoverTrigger>
            <PopoverContent
              side="bottom"
              align="end"
              collisionPadding={12}
              className="max-h-[70vh] w-[calc(100vw-2rem)] max-w-lg overflow-y-auto p-3"
            >
              <nav aria-label="Child record groups">
                {rootGroup && <RecordFileExplorer
                  key={rootGroup.id}
                  group={rootGroup}
                  currentGroupId={routeGroupId ?? group?.id ?? rootGroup.id}
                  activeGroupPathIds={activeGroupPathIds}
                  lockedResourceIds={lockedResourceIds}
                />}
              </nav>
            </PopoverContent>
          </Popover>
        )}
    </div>
  )

  return (
    <aside className="flex w-full shrink-0 flex-col gap-3 bg-background p-3 md:gap-0 md:bg-muted/30 md:p-0 md:w-80">
      {groupSummaryCard}
      <nav aria-label="Record group sections" className="hidden flex-col gap-0.5 px-2 pb-2 md:flex">
        {sections.map(({ value, label, icon: Icon, to, active }) => (
          <Link
            key={value}
            to={to}
            aria-current={active ? 'page' : undefined}
            className={cn(
              'flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm font-medium transition-colors',
              active ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-accent hover:text-foreground',
            )}
          >
            <Icon aria-hidden="true" className="h-4 w-4 shrink-0" />
            {label}
          </Link>
        ))}
      </nav>
      <div className="hidden min-h-0 flex-1 overflow-y-auto px-2 pb-3 md:block">
        <nav aria-label="Child record groups">
          {rootGroup && (
            <RecordFileExplorer
              key={rootGroup.id}
              group={rootGroup}
              currentGroupId={routeGroupId ?? group?.id ?? rootGroup.id}
              activeGroupPathIds={activeGroupPathIds}
              lockedResourceIds={lockedResourceIds}
            />
          )}
        </nav>
      </div>
    </aside>
  )
}
